import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { useAuth } from '@drycut/auth';
import {
  CheckInDocument,
  type CompleteStylistFragment,
  GetCheckedInStylistsDocument,
  GetStoreDocument,
  GetStylistsDocument,
} from '@drycut/graphql';
import { Button, Heading, Modal, Para, Stack } from '@drycut/ui-components';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';

import { StylistCheckInGrid } from './StylistCheckInGrid';

export function CheckInModal(): React.ReactElement {
  const { currentSlug } = useAuth();
  const [selectedStylist, setSelectedStylist] = useState<CompleteStylistFragment | null>(null);
  const [open, setOpen] = useState(false);
  const [checkIn, { loading: checkingIn }] = useMutation(CheckInDocument);

  const [getStylists, { loading, data }] = useLazyQuery(GetStylistsDocument, {
    fetchPolicy: 'network-only', // Always fetch the latest data from the server
  });

  const { data: storeData } = useQuery(GetStoreDocument, {
    variables: { slug: currentSlug || '' },
  });

  // On button click fetch the available stylists and open the modal once the data is loaded
  const handleOpen = async () => {
    setSelectedStylist(null);
    await getStylists({ variables: { onlyCheckedOut: true, onlyScheduled: true } });
    setOpen(true);
  };

  const handleCheckIn = async () => {
    if (!selectedStylist) return;

    await checkIn({
      variables: {
        input: {
          stylistId: selectedStylist.id,
        },
      },
      refetchQueries: [GetCheckedInStylistsDocument],
    });

    setOpen(false);
  };

  const stylists = data?.stylists?.nodes || [];
  const filteredStylists = stylists.filter((stylist): stylist is CompleteStylistFragment =>
    Boolean(stylist),
  );

  const store = storeData?.store;

  const title = (
    <Stack>
      <Heading variant="h2" align="left">
        <Stack as="span" direction="row" className="items-center">
          {store?.name} <ChevronRightIcon className="w-12 h-12" /> Check in
        </Stack>
      </Heading>
      <Para align="left">Wähle dein Profil aus</Para>
    </Stack>
  );

  return (
    <>
      <Button color="primary" loading={loading} onClick={handleOpen} className="w-48">
        Check in
      </Button>

      <Modal
        title={title}
        open={open}
        onClose={() => setOpen(false)}
        closeButton
        className="max-w-full w-10/12"
      >
        <StylistCheckInGrid
          storeSlug={currentSlug || ''}
          stylists={filteredStylists}
          onSelect={setSelectedStylist}
        />

        <Stack className="items-center mt-10">
          <Button
            className="w-fit inline-block"
            loading={checkingIn}
            disabled={!selectedStylist}
            onClick={handleCheckIn}
          >
            Check in @ {store?.name}
          </Button>
        </Stack>
      </Modal>
    </>
  );
}
